/** Recurse through context redact any sensitive information
 *
 * Keys to redact:
 * - authorization
 * - x-client-id
 *
 * Partial keys to redact:
 * - auth
 * - secret
 * - token
 *
 * Partial values to redact:
 * - bearer
 */
export default function sanitizeContext(
  context: Record<string, any>
): Record<string, any> {
  const visitedObjects = new WeakSet();

  function sanitizeValue(value: any, visited: WeakSet<object>): any {
    if (value == null) {
      return value;
    }

    // Primitive types
    if (typeof value !== 'object') {
      return typeof value === 'string' && value.toLowerCase().includes('bearer')
        ? '[REDACTED]'
        : value;
    }

    // Circular references
    if (visited.has(value)) {
      return '[CIRCULAR]';
    }

    // Arrays
    if (Array.isArray(value)) {
      visited.add(value);
      const sanitizedArray = value.map(item => sanitizeValue(item, visited));
      visited.delete(value);

      return sanitizedArray;
    }

    // Objects
    visited.add(value);
    const sanitizedObj: Record<string, any> = {};

    for (const [key, val] of Object.entries(value)) {
      const lowerKey = key.toLowerCase();

      const exactKeyMatches = ['authorization', 'x-client-id'].some(
        exact => lowerKey === exact
      );
      const partialKeyMatches = ['auth', 'secret', 'token'].some(partial =>
        lowerKey.includes(partial)
      );

      if (exactKeyMatches || (partialKeyMatches && typeof val === 'string')) {
        sanitizedObj[key] = '[REDACTED]';
        continue;
      }

      // Recursively sanitize the value
      sanitizedObj[key] = sanitizeValue(val, visited);
    }

    visited.delete(value);

    return sanitizedObj;
  }

  return sanitizeValue(context, visitedObjects);
}
